import React, { lazy, Suspense, useEffect, useState } from 'react';
import './App.css';
import { 
  BrowserRouter as Router, 
  Route, 
  Switch, 
  Redirect,
  useLocation,
  useHistory
} from 'react-router-dom';
import SpinningLoader from './components/common/elements/SpinningLoader';
import SiteWrapper from './components/layout/SiteWrapper';
import Dashboard from './page-conteners/Dashboard/Dashboard';
import { AuthProvider } from './components/context/authContext';
import PrivateRoute from './components/Auth/PrivateRoute';
import Faq from './page-conteners/OtherPages/Faq';
import CallBack from './page-conteners/Authentication/CallBack';


const DeviceData = lazy(() => import('./page-conteners/OtherPages/DeviceData'));
const AlertInfo = lazy(() => import('./page-conteners/OtherPages/AlertInfo')); 
const Graph = lazy(() => import('./page-conteners/OtherPages/Graph'));
const CloseTab = lazy(() => import('./page-conteners/Authentication/CloseTab'));

const getRandomUUID = () => {
  if (crypto && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

const AuthHandler = () => {
  const location = useLocation();
  
  useEffect(() => {
    const authChannel = new BroadcastChannel('auth_channel');
    
    authChannel.onmessage = (event) => {
      if (event.data.type === 'logout') {
        sessionStorage.clear();
        localStorage.setItem('app-logout-event', Date.now());
        window.location.href = '/close-tab';
      }
    };
    
    const handleStorageChange = (e) => {
      if (e.key === 'app-logout-event') {
        sessionStorage.clear();
        window.location.href = '/close-tab';
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    if (location.pathname === '/callback' || location.pathname === '/close-tab') return;
    
    const params = new URLSearchParams(location.search);
    const error = params.get('error');
    
    if (error) {
      console.error('Authentication error:', error);
      sessionStorage.removeItem('isLoggedIn');
      sessionStorage.removeItem('oauth_state');
      sessionStorage.removeItem('access_token');
      
      if (location.pathname !== '/login') {
        window.location.href = `/login?error=${encodeURIComponent(error)}`;
      }
      return;
    }
    
    const isLoggedIn = sessionStorage.getItem('isLoggedIn') === 'true';
    if (isLoggedIn) return;

    if (!params.get('code') && location.pathname !== '/login') {
      const clientId = process.env.REACT_APP_CLIENT_ID;
      const redirectUri = encodeURIComponent(process.env.REACT_APP_REDIRECT_URI);
      const authServer = process.env.REACT_APP_AUTH_SERVER;
      const scope = encodeURIComponent('read write');
      const state = getRandomUUID();

      sessionStorage.setItem('oauth_state', state);
      sessionStorage.setItem('oauth_state_timestamp', Date.now().toString());
      
      window.location.href = `${authServer}/oauth2/authorize?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}&scope=${scope}&state=${state}`;
    }
    
    return () => {
      authChannel.close();
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [location]);

  return null;
};

const LogoutHandler = () => {
  const history = useHistory();
  
  useEffect(() => {
    const performLogout = () => {
      sessionStorage.clear();
      
      const authChannel = new BroadcastChannel('auth_channel');
      authChannel.postMessage({ type: 'logout' });
      
      localStorage.setItem('app-logout-event', Date.now());
      
      const clientId = process.env.REACT_APP_CLIENT_ID;
      const authServer = process.env.REACT_APP_AUTH_SERVER;
      const redirectUri = encodeURIComponent(`${window.location.origin}/close-tab`);
      
      const logoutUrl = `${authServer}/logout?client_id=${clientId}&post_logout_redirect_uri=${redirectUri}`;
      
      setTimeout(() => {
        window.location.href = logoutUrl;
      }, 100);
      
      setTimeout(() => authChannel.close(), 1000);
    };
    
    performLogout();
  }, [history]);
  
  return (
    <div className="logout-container">
      <p>Logging out and closing session...</p>
      <SpinningLoader />
    </div>
  );
};

const LoginPage = () => {
  const [error, setError] = useState(null);
  const location = useLocation();
  
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const errorParam = params.get('error');
    
    if (errorParam) {
      setError(errorParam);
      const cleanUrl = window.location.origin + window.location.pathname;
      window.history.replaceState(null, '', cleanUrl);
    }
  }, [location]);

  const isLoggedIn = sessionStorage.getItem('isLoggedIn') === 'true';
  if (isLoggedIn) {
    return <Redirect to="/plant-monitoring/dashboard" />;
  }

  return (
    <div className="login-container">
      {error ? (
        <div className="error-container">
          <h3>Login Failed</h3>
          <p>Error: {error}</p>
          <button 
            onClick={() => {
              const clientId = process.env.REACT_APP_CLIENT_ID;
              const redirectUri = encodeURIComponent(process.env.REACT_APP_REDIRECT_URI);
              const authServer = process.env.REACT_APP_AUTH_SERVER;
              const scope = encodeURIComponent('read write');
              const state = getRandomUUID();
              
              sessionStorage.setItem('oauth_state', state);
              sessionStorage.setItem('oauth_state_timestamp', Date.now().toString());
              
              window.location.href = `${authServer}/oauth2/authorize?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}&scope=${scope}&state=${state}`;
            }}
            style={{ padding: '10px 20px', marginTop: '20px' }}
          >
            Retry Login
          </button>
        </div>
      ) : (
        <div className="auth-redirect">
          <p>Redirecting to authentication...</p>
          <SpinningLoader />
        </div>
      )}
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <AuthHandler />
        <Suspense fallback={<SpinningLoader />}>
          <Switch>
            <Route exact path="/callback" component={CallBack} />
            <Route exact path="/close-tab" component={CloseTab} />
            <Route exact path="/logout" component={LogoutHandler} />
            
            <Route exact path="/" render={() => {
              const isLoggedIn = sessionStorage.getItem('isLoggedIn') === 'true';
              return isLoggedIn 
                ? <Redirect to="/plant-monitoring/dashboard" /> 
                : <Redirect to="/login" />;
            }} />
            
            <Route exact path="/login" component={LoginPage} />

            <SiteWrapper>
              <PrivateRoute exact path="/plant-monitoring/dashboard" component={Dashboard} />
              <PrivateRoute exact path="/plant-monitoring/monitoring/devicedata" component={DeviceData} />
              <PrivateRoute exact path="/plant-monitoring/monitoring/alerts/alertInfo" component={AlertInfo} />
              <PrivateRoute exact path="/plant-monitoring/monitoring/graph" component={Graph} />
              <PrivateRoute exact path="/plant-monitoring/monitoring/faq" component={Faq} />

            </SiteWrapper>

            <Route render={() => <Redirect to="/" />} />
          </Switch>
        </Suspense>
      </Router>
    </AuthProvider>
  );
}

export default App;

